import { useState } from 'react'
import { Lock } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import { Label } from '@/components/ui/Label'
import { ApiError } from '@/api/client'
import { createAccessRequest } from './api'
import type { AccessRequestResource } from './types'

interface Props {
  /** What kind of resource the user is asking for access to. */
  resourceType: AccessRequestResource
  /** Resource public_id (UUID). */
  resourceId: string
  /** The permission scope being requested, e.g. 'cases:write'. */
  scope: string
  /** Short human label for the resource — shown in the dialog. */
  resourceLabel?: string
  /** Override the default button label. */
  buttonLabel?: string
  /** Compact = icon + thinner padding (for inline header use). */
  compact?: boolean
}

const WINDOW_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
  { value: 240, label: '4 hours' },
  { value: 480, label: '8 hours' },
  { value: 1440, label: '24 hours (max)' },
]

/**
 * RequestAccessButton — inline button + dialog that opens a JIT
 * access request scoped to one resource. Drop it next to any
 * permission-gated affordance so the user can ask for access
 * without leaving the page.
 *
 *   <RequestAccessButton
 *     resourceType="case"
 *     resourceId={c.id}
 *     scope="cases:write"
 *     resourceLabel={c.case_number}
 *   />
 *
 * On submit the dialog POSTs to /access-requests and surfaces a
 * toast with the request id; the approver sees it in their
 * /access-requests inbox.
 */
export function RequestAccessButton({
  resourceType,
  resourceId,
  scope,
  resourceLabel,
  buttonLabel,
  compact,
}: Props) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button
        variant={compact ? 'ghost' : 'outline'}
        size="sm"
        onClick={() => setOpen(true)}
      >
        <Lock size={12} aria-hidden />
        {buttonLabel ?? 'Request access'}
      </Button>
      {open ? (
        <RequestAccessDialog
          resourceType={resourceType}
          resourceId={resourceId}
          scope={scope}
          resourceLabel={resourceLabel}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  )
}

function RequestAccessDialog({
  resourceType,
  resourceId,
  scope,
  resourceLabel,
  onClose,
}: {
  resourceType: AccessRequestResource
  resourceId: string
  scope: string
  resourceLabel?: string
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [reason, setReason] = useState('')
  const [windowMinutes, setWindowMinutes] = useState(60)

  const mutation = useMutation({
    mutationFn: () =>
      createAccessRequest({
        resource_type: resourceType,
        resource_id: resourceId,
        scope,
        reason: reason.trim(),
        window_minutes: windowMinutes,
      }),
    onSuccess: (req) => {
      toast.success(
        `Request opened (id ${req.id.slice(0, 8)}). Track it under your access requests.`,
      )
      void qc.invalidateQueries({ queryKey: ['access-requests', 'mine'] })
      onClose()
    },
    onError: (err) => {
      toast.error(
        err instanceof ApiError
          ? err.message || 'Could not open the request.'
          : err instanceof Error
            ? err.message
            : 'Could not open the request.',
      )
    },
  })

  const valid = reason.trim().length >= 10

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Request access</DialogTitle>
          <DialogDescription>
            Ask a senior analyst for {scope} on{' '}
            {resourceLabel ? <code>{resourceLabel}</code> : `this ${resourceType}`}.
            Approvers see the request in their inbox; you'll see the decision
            on your /access-requests page.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault()
            if (valid) mutation.mutate()
          }}
        >
          <div className="grid grid-cols-2 gap-2 rounded-md border border-border bg-muted/30 p-2 text-xs">
            <Stat label="Resource" value={`${resourceType} · ${resourceLabel ?? resourceId.slice(0, 8)}`} />
            <Stat label="Scope" value={scope} mono />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="ar-reason">Reason (≥10 chars)</Label>
            <textarea
              id="ar-reason"
              // eslint-disable-next-line jsx-a11y/no-autofocus -- modal/dialog focus management; primary control on open
              autoFocus
              rows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain why you need this access — the approver reads this verbatim."
              className="w-full rounded border border-input bg-card px-3 py-2 text-sm"
            />
            <p className="text-[0.6875rem] text-muted-fg">
              {reason.trim().length} / 10 characters
            </p>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="ar-window">Access window</Label>
            <select
              id="ar-window"
              value={windowMinutes}
              onChange={(e) => setWindowMinutes(Number(e.target.value))}
              className="rounded border border-input bg-card px-3 py-2 text-sm"
            >
              {WINDOW_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <p className="text-[0.6875rem] text-muted-fg">
              Access auto-expires after this window. You can re-request if
              you need more time.
            </p>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={!valid || mutation.isPending}>
              {mutation.isPending ? 'Submitting…' : 'Send request'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function Stat({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <p className="text-[0.625rem] uppercase tracking-wide text-muted-fg">
        {label}
      </p>
      <p className={`mt-0.5 break-all text-xs ${mono ? 'font-mono' : ''}`}>
        {value}
      </p>
    </div>
  )
}
